import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

const MusicPlayerContext = createContext();

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
};

const playableTracks = (tracks) =>
  (tracks || []).filter((track) => track?.url);

export const MusicPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  const playlistRef = useRef(playlist);
  const currentIndexRef = useRef(currentIndex);
  playlistRef.current = playlist;
  currentIndexRef.current = currentIndex;

  const playTrack = useCallback((track, queue = null) => {
    setIsPlaying(false);

    if (Array.isArray(queue)) {
      const nextPlaylist = playableTracks(queue);
      const index = nextPlaylist.findIndex((item) => item.id === track.id);
      const resolvedIndex = index >= 0 ? index : 0;
      const resolvedTrack = index >= 0 ? track : nextPlaylist[0];

      if (!resolvedTrack?.url) {
        return;
      }

      playlistRef.current = nextPlaylist;
      currentIndexRef.current = resolvedIndex;
      setPlaylist(nextPlaylist);
      setCurrentIndex(resolvedIndex);
      setCurrentTrack(resolvedTrack);
      setIsPlaying(true);
      return;
    }

    if (!track?.url) {
      return;
    }

    setPlaylist((prev) => {
      const index = prev.findIndex((item) => item.id === track.id);
      if (index >= 0) {
        currentIndexRef.current = index;
        setCurrentIndex(index);
        return prev;
      }

      const nextIndex = prev.length;
      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
      return [...prev, track];
    });
    setCurrentTrack(track);
    setIsPlaying(true);
  }, []);

  const playPause = useCallback(() => {
    setIsPlaying((playing) => (currentTrack ? !playing : false));
  }, [currentTrack]);

  const playNext = useCallback(() => {
    const list = playlistRef.current;
    const index = currentIndexRef.current;

    if (list.length === 0 || index >= list.length - 1) {
      return;
    }

    const nextIndex = index + 1;
    currentIndexRef.current = nextIndex;
    setCurrentIndex(nextIndex);
    setCurrentTrack(list[nextIndex]);
    setIsPlaying(true);
  }, []);

  const playPrevious = useCallback(() => {
    const list = playlistRef.current;
    const index = currentIndexRef.current;

    if (list.length === 0 || index <= 0) {
      return;
    }

    const prevIndex = index - 1;
    currentIndexRef.current = prevIndex;
    setCurrentIndex(prevIndex);
    setCurrentTrack(list[prevIndex]);
    setIsPlaying(true);
  }, []);

  const addToPlaylist = useCallback((track) => {
    if (!track?.url) {
      return;
    }

    setPlaylist((prev) => {
      if (prev.find((item) => item.id === track.id)) {
        return prev;
      }
      return [...prev, track];
    });
  }, []);

  const removeFromPlaylist = useCallback((trackId) => {
    setPlaylist((prev) => prev.filter((item) => item.id !== trackId));

    if (currentTrack?.id === trackId) {
      setCurrentTrack(null);
      setIsPlaying(false);
      currentIndexRef.current = 0;
      setCurrentIndex(0);
    }
  }, [currentTrack]);

  const clearPlaylist = useCallback(() => {
    playlistRef.current = [];
    currentIndexRef.current = 0;
    setPlaylist([]);
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentIndex(0);
  }, []);

  const setVolumeLevel = useCallback((newVolume) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((muted) => !muted);
  }, []);

  const value = {
    currentTrack,
    isPlaying,
    playlist,
    currentIndex,
    volume,
    isMuted,
    playTrack,
    playPause,
    playNext,
    playPrevious,
    addToPlaylist,
    removeFromPlaylist,
    clearPlaylist,
    setVolumeLevel,
    toggleMute,
    setIsPlaying,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};
